import React, { useEffect, useRef, useState } from 'react';

// Design-system token layer (Step 0) — imported BEFORE App.css so the legacy
// :root aliases in App.css resolve against the --cyv-* tokens. Pure custom
// properties (additive); fonts.css intentionally skipped (Josefin already
// loaded; --font-script overridden to the repo's Typekit face, not Breathing).
import './styles/tokens/colors.css';
import './styles/tokens/typography.css';
import './styles/tokens/spacing.css';
import './App.css';
import './styles/utilities.css';
import './styles/cyv-utilities.css'; // new brand utility classes (Step 0b)
import {
	Container,
	Row,
	Col,
	Image,
	Button,
	Navbar,
	Nav,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import ScrollToTopButton from './components/ScrollUp/ScrollToButton';
import ScrollToTop from './components/ScrollUp/ScrollToTop';
import AnimatedRoutes from './components/Routes/AnimatedRoutes';
import SwNotificationClickBridge from './components/Portal/SwNotificationClickBridge';
import UpdateBanner from './components/UpdateBanner/UpdateBanner';
import { AuthProvider } from './contexts/AuthContext/AuthContext';
import NavAuthButtons from './components/Nav/NavAuthButtons';
import NavbarBottom from './components/Nav/NavbarBottom';
import DevLaunchpad from './components/Dev/DevLaunchpad';
import { isDevPreview } from './utils/devPreview';
import { normalizePathname } from './utils/normalizePathname';
import { isNoindexPath } from './utils/noindexRoutes';
import useMetaPixel from './hooks/useMetaPixel';

import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/pro-regular-svg-icons';
import './Home.css';

import logoShort from './assets/LOGOshort.png';

function App() {
	useEffect(() => {
		window.history.scrollRestoration = 'manual';
	}, []);

	// The employee portal runs inside its own persistent shell (PortalLayout),
	// which provides the nav + header. Suppress the marketing-site chrome
	// (top nav, mobile drawer, footer) there so we don't stack two top bars —
	// important in standalone PWA / App Mode — and let the shell go full-bleed.
	const location = useLocation();

	// Meta Pixel: base init + SPA PageView re-fire, marketing routes only.
	// Self-guards on REACT_APP_META_PIXEL_ID (no-op when unset) and skips the
	// /portal, /login, and noindex app surfaces internally.
	useMetaPixel();

	/* Trailing slashes are stripped ONCE here and every path test below reads
	   this, not location.pathname. See normalizePathname for why. */
	const pathname = normalizePathname(location.pathname);

	// Portal app-shell routes run inside their own persistent .portal-shell
	// chrome (the staff dashboard at /portal/work, the Phase B private spaces,
	// and the hub), so suppress the marketing-site chrome (top nav, drawer,
	// footer, dev launchpad) there to avoid stacking two top bars — important in
	// standalone PWA / App Mode. Legacy /portal/employee/* now redirects into
	// /portal/work; keep it suppressed so the redirect frame never flashes the
	// marketing nav.
	const isPortalAppShell =
		pathname.startsWith('/portal/work') ||
		pathname.startsWith('/portal/customer') ||
		pathname.startsWith('/portal/employee') ||
		pathname.startsWith('/portal/personal') ||
		pathname.startsWith('/portal/owners') ||
		pathname.startsWith('/portal/barbara') ||
		pathname.startsWith('/portal/bible') ||
		pathname === '/portal' ||
		// The lesson stage window (pass 3 release 5): full-bleed, no site chrome.
		pathname === '/stage' ||
		// The detached lyrics window (2026-10-05): the lesson's column in a
		// window of its own, no site chrome either.
		pathname === '/lyrics-window';

	// Robots noindex route set — see utils/noindexRoutes (own module, so the
	// jsdom pin can exercise it without mounting the app). Additive metadata
	// only — no routing/auth change.
	const isNoindexRoute = isNoindexPath(pathname);

	// The month the seasonal nav flags below read. RESTORED 2026-08-21 along with
	// showFallNav; it had been removed while both flags were forced off, rather
	// than left dangling as an unread binding. showSummerNav is still forced off,
	// so today this has exactly one reader — do not remove it again without
	// checking both flags below.
	//
	// getMonth() is 0-indexed: 0 = January, 7 = August, 8 = September.
	const currentMonth = new Date().getMonth();

	// Seasonal: surface the Summer Workshops link April–August.
	//
	// HELD OFF deliberately (Anthony, 2026-08-08): the fall paid-acquisition push
	// starts Aug 9 and every marketing surface now has to read as fall. August is
	// still INSIDE the seasonal window below, so this had to be forced off rather
	// than left to expire on its own — a month from now it would have gone quiet
	// by itself, which is exactly the nine days we cannot wait.
	//
	// The ROUTE stays live at /summer (and /summer/:workshopId) on purpose — same
	// posture /fall is held in: reachable by URL, absent from nav,
	// sitemap and inbound links. It is NOT an orphan we forgot to delete. Camp
	// week runs Aug 16–21 and the workshop Stripe checkout's success_url and
	// cancel_url are `${origin}/summer/${workshopId}` baked into a DEPLOYED Cloud
	// Function (functions/index.js:1170) — deleting the route would land a family
	// returning from a real payment on a dead page, and fixing that would cost the
	// functions deploy this whole change exists to avoid.
	// TO RESTORE next spring: flip this back to the seasonal window below.
	//   const showSummerNav = currentMonth >= 3 && currentMonth <= 7;
	const showSummerNav = false;

	// Seasonal: surface the /fall season-information link August–September, the
	// window in which returning families are actually looking for it. The page
	// stays reachable by URL year-round — the welcome email links straight to it
	// — so dropping out of the nav hides it from browsing, never from the link.
	//
	// ANNOUNCED 2026-08-21 — restored to the seasonal window. It had been held off
	// since 2026-08-07 because the page told families to watch for a claim-link
	// email that had not been sent, so a parent who found it by browsing would be
	// told to wait for something that was not coming. The walkthrough video went
	// in on the same deploy, which is what made the page worth finding; the
	// welcome email follows this deploy rather than preceding it.
	//
	// The window is August–September, when returning families are actually
	// looking for this. The page stays reachable by URL year-round — the welcome
	// email links straight to it — so dropping out of the nav in October hides it
	// from browsing, never from the link.
	const showFallNav = currentMonth >= 7 && currentMonth <= 8;

	// Mobile nav drawer (Web Awesome). Control it via its `open` property — the
	// documented React path for WA overlays — and mirror dismissals (backdrop /
	// Esc / close button) back into state so the burger and drawer stay in sync.
	const [drawerOpen, setDrawerOpen] = useState(false);
	const drawerRef = useRef(null);

	useEffect(() => {
		const drawer = drawerRef.current;
		if (drawer) drawer.open = drawerOpen;
	}, [drawerOpen]);

	useEffect(() => {
		const drawer = drawerRef.current;
		if (!drawer) return;
		const handleHide = () => setDrawerOpen(false);
		drawer.addEventListener('wa-after-hide', handleHide);
		return () => drawer.removeEventListener('wa-after-hide', handleHide);
	}, []);

	const closeDrawer = () => setDrawerOpen(false);

	// One link list, rendered in both the desktop nav and the mobile drawer.
	const navLinks = [
		{ to: '/', label: 'Home', scrollTop: true },
		{ to: '/about', label: 'About' },
		...(showSummerNav ? [{ to: '/summer', label: 'Summer' }] : []),
		...(showFallNav ? [{ to: '/fall', label: 'Fall Info' }] : []),
		{ to: '/lessons', label: 'Lessons' },
		{ to: '/faculty', label: 'Faculty' },
		{ to: '/testimonials', label: 'Testimonials' },
		{ to: '/faq', label: 'FAQ' },
		{ to: '/contact', label: 'Contact' },
	];

	return (
		<>
			<SwNotificationClickBridge />
			{/* "A new version of this page is available" (2026-09-29). */}
			<UpdateBanner pathname={pathname} />
			{/* defer={false} is mandatory on every Helmet — deferred commits
			    ride requestAnimationFrame and never land in a hidden or
			    frame-starved tab. Full rationale in Seo.js. */}
			<Helmet defer={false}>
				<meta charSet='utf-8' />
				<link rel='icon' href='/favicon.ico' />
				<meta
					name='viewport'
					content='width=device-width, initial-scale=1'
				/>
				<title>Voice Lessons in Burnsville, MN | Color Your Voice</title>
				<meta
					name='description'
					content='Private voice lessons, summer workshops, and audition prep for young singers in Burnsville, MN. Now enrolling at Color Your Voice.'
				/>
				<meta
					property='og:image'
					content='/share-img.jpg'
				/>
				{isNoindexRoute && (
					<meta name='robots' content='noindex,nofollow' />
				)}
			</Helmet>
			<div className='d-flex flex-column min-vh-100 text-center tight-gutter'>
				<Container
					fluid={isPortalAppShell}
					className={
						isPortalAppShell
							? 'd-flex flex-column w-100 p-0 flex-grow-1'
							: 'd-flex flex-column w-100 p-3 mx-auto flex-grow-1'
					}>
					{!isPortalAppShell && (
					<>
					<Navbar
						expand='md'
						bg=''
						variant='light'
						className='mb-4 tight-gutter'
						sticky>
						<Container>
							<Navbar.Brand
								as={Link}
								to='/'
								onClick={() => window.scrollTo(0, 0)}>
								<Image
									src={logoShort}
									alt='Logo'
									style={{ height: '32px', width: 'auto' }}
								/>
							</Navbar.Brand>
							{/* Desktop nav (md+) */}
							<Nav className='ms-auto d-none d-md-flex align-items-center'>
								{navLinks.map((l) => (
									<Nav.Link
										key={l.to}
										as={Link}
										to={l.to}
										onClick={
											l.scrollTop
												? () => window.scrollTo(0, 0)
												: undefined
										}>
										{l.label}
									</Nav.Link>
								))}
								<NavAuthButtons />
							</Nav>

							{/* Mobile hamburger → opens the WA drawer (below md) */}
							<button
								type='button'
								className='nav-burger d-md-none ms-auto'
								aria-label='Open menu'
								aria-expanded={drawerOpen}
								onClick={() => setDrawerOpen(true)}>
								<FontAwesomeIcon icon={faBars} />
							</button>
						</Container>
					</Navbar>

					{/* Mobile nav drawer (Web Awesome) — closes on link tap,
					    which fixes the menu-stays-open-after-navigation bug. */}
					<wa-drawer
						ref={drawerRef}
						label='Menu'
						placement='end'
						id='nav-drawer'
						className='nav-drawer'>
						<Nav className='flex-column nav-drawer-links'>
							{navLinks.map((l) => (
								<Nav.Link
									key={l.to}
									as={Link}
									to={l.to}
									onClick={() => {
										if (l.scrollTop) window.scrollTo(0, 0);
										closeDrawer();
									}}>
									{l.label}
								</Nav.Link>
							))}
							<div
								className='nav-drawer-auth'
								onClick={closeDrawer}>
								<NavAuthButtons />
							</div>
						</Nav>
					</wa-drawer>
					</>
					)}

					<AnimatedRoutes />

				</Container>

				{/* Marketing footer is suppressed in the employee portal — the
				    shell is a self-contained app surface. */}
				{!isPortalAppShell && <NavbarBottom />}
				<ScrollToTop />
				{/* PWA install promotion is deferred to fall (#25): the installed
					app currently lands on a portal login customers can't use yet.
					InstallPrompt stays in the tree but is not rendered for summer;
					PWA infra (manifest/SW) is untouched so installs still load. */}
				{/* Dev-only launchpad: one-click links to every route. Gated by
					isDevPreview() (build-time NODE_ENV) — never renders in prod. */}
				{/* Suppressed in the employee portal: its fixed bottom-left FAB
						overlapped the scheduling/calendar content during phone/LAN
						dev testing, and the portal has its own sidebar nav. (Prod
						PWA never renders it regardless of route.) */}
					{isDevPreview() && !isPortalAppShell && <DevLaunchpad />}
			</div>
		</>
	);
}

export default App;
